import type { SharedTableState, Status } from '@/model/sourceState/sourcestate.generated';
import { toSourceIssueView } from '@/viewmodel/features/sourceState/mappers/issue';
import { toSourceStatusView } from '@/viewmodel/features/sourceState/mappers/status';
import type {
  SharedStateSummaryView,
  SharedTableStateView,
  StatusTone,
} from '@/viewmodel/features/sourceState/types';

const STATUS_RANK: Readonly<Record<string, number | undefined>> = {
  ready: 0,
  syncing: 1,
  pending_setup: 2,
  disabled: 3,
  unavailable: 4,
  deleting: 5,
  attention: 6,
  failed: 7,
} satisfies Record<Status, number>;

const ISSUE_RANK: Record<StatusTone, number> = {
  neutral: 0,
  success: 0,
  info: 1,
  warning: 2,
  danger: 3,
};

export function toSharedTableStateView(table: SharedTableState): SharedTableStateView {
  return {
    providerProjectName: table.provider_project_name,
    name: table.name,
    status: toSourceStatusView(table),
    issue: toSourceIssueView(table.issue),
    lastSuccessfulSyncAt: table.last_successful_sync_at ?? undefined,
  };
}

function rankOf(table: SharedTableStateView): number {
  const status = STATUS_RANK[table.status.status] ?? 0;

  return status * 10 + (table.issue ? ISSUE_RANK[table.issue.tone] : 0);
}

function worstOf(tables: readonly SharedTableStateView[]): SharedTableStateView | undefined {
  return tables.reduce<SharedTableStateView | undefined>(
    (worst, table) => (worst === undefined || rankOf(table) > rankOf(worst) ? table : worst),
    undefined,
  );
}

function oldestOf(dates: readonly (string | undefined)[]): string | undefined {
  return dates.reduce<string | undefined>((oldest, date) => {
    if (!date) return oldest;
    return oldest === undefined || Date.parse(date) < Date.parse(oldest) ? date : oldest;
  }, undefined);
}

export function summariseSharedTables(
  tables: readonly (SharedTableStateView | undefined)[],
): SharedStateSummaryView {
  const known = tables.flatMap((table) => (table ? [table] : []));
  const worst = worstOf(known);
  const isStale = known.some((table) => table.status.isStale);

  return {
    status: worst ? { ...worst.status, isStale } : undefined,
    issue: worstOf(known.filter((table) => table.issue))?.issue,
    lastSuccessfulSyncAt: oldestOf(known.map((table) => table.lastSuccessfulSyncAt)),
  };
}
